// import React, { useContext } from 'react';
import React, { useContext, useEffect, useRef } from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import Logo from './Logo';
import { ThemeContext } from '../context/ThemeContext';
import goldLeafImg from '../assets/images/gold-leaf.jpg';

const MoonIcon = () => {
  const gradientRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!gradientRef.current) return;
      const x = e.clientX / window.innerWidth;
      const y = e.clientY / window.innerHeight;
      
      // Move the focal point of the radial gradient based on mouse position
      // This simulates a light source reflecting off the gold leaf
      const fx = 20 + (x * 60);
      const fy = 20 + (y * 60);

      gradientRef.current.setAttribute('fx', `${fx}%`);
      gradientRef.current.setAttribute('fy', `${fy}%`);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <svg width="3rem" height="3rem" viewBox="0 0 1200 1200" xmlns="http://www.w3.org/2000/svg" overflow="visible"> 
      <defs>
        <pattern id="gold-leaf-pattern" patternUnits="userSpaceOnUse" width="4000" height="4000">
          <image href={goldLeafImg} width="4000" height="4000" preserveAspectRatio="xMidYMid slice" />
        </pattern>
        <radialGradient ref={gradientRef} id="gold-leaf" cx="45%" cy="46%" r="60%" fx="30%" fy="40%">
          <stop offset="20%" stopColor="#e5d0a9" />
          <stop offset="100%" stopColor="#d4d18000" />
        </radialGradient>
      </defs>
      <path d="m69.902 1006c112.29 119.46 271.56 193.97 448.41 193.97 696.7 0 848.5-983.34 193.24-1200 359.4 468.32-65.145 1133.5-641.65 1006z" 
      fill="none" fillRule="evenodd" stroke="var(--hover)" stroke-width="100" stroke-linejoin="round" opacity="1"
      />
      
      <path d="m69.902 1006c112.29 119.46 271.56 193.97 448.41 193.97 696.7 0 848.5-983.34 193.24-1200 359.4 468.32-65.145 1133.5-641.65 1006z" 
      fill="url(#gold-leaf-pattern)" fillRule="evenodd" 
      />
      <path d="m69.902 1006c112.29 119.46 271.56 193.97 448.41 193.97 696.7 0 848.5-983.34 193.24-1200 359.4 468.32-65.145 1133.5-641.65 1006z" 
      fill="url(#gold-leaf)" fillRule="evenodd" 
      />
    </svg>
  );
};

const NavStyles = styled.nav`
  margin: 2rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-template-rows: auto auto;
  align-items: center;

  .logo {
    grid-column: 2;
    grid-row: 1;
    text-align: center;
  }

  .logo a:hover,
  .logo a[aria-current="page"] {
    background-color: transparent;
  }

  .top-left {
    grid-column: 1;
    grid-row: 1;
    justify-self: start;
    display: flex;
    align-items: center;
  }

  .top-right {
    grid-column: 3;
    grid-row: 1;
    justify-self: end;
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  ul.nav {
    grid-column: 1 / -1;
    grid-row: 2;
    display: flex;
    justify-content: center;
    gap: 2rem;
    list-style: none;
    padding: 0;
    /* margin-top: 1rem; */
  }

  .separator {
    color: #a3915a;
    display: flex;
    align-items: center;
  }

  a, button.theme-toggle {
    color: var(--body);
    padding: 1.5rem;
    text-align: center;
    vertical-align: middle;
    margin: auto;
    border-radius: 1rem;

    &:hover,
    &[aria-current="page"] {
      background-color: var(--hover);
    }
  }
  
  button.theme-toggle {
      background: transparent;
      border: none;
      font-size: 2rem;
      cursor: pointer;
      box-shadow: none;
      --cast: 0;
  }

  @media (max-width: 550px) {
    margin: 2rem 0;
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;

    .logo {
      grid-column: 1;
      grid-row: 1;
      justify-self: left;
      margin-left: 2rem;
    }
    
    .top-left {
      grid-column: 1;
      grid-row: 3;
      justify-self: center;
      margin-top: 1rem;
    }
    
    .top-right {
      grid-column: 1;
      grid-row: 1;
      justify-self: right;
      margin-right: 2rem;
    }
    
    ul.nav {
      grid-column: 1;
      grid-row: 2;
      flex-direction: column;
      align-items: center;
      gap: 2rem;
    }
    
    ul.nav a {
      padding: 1rem;
    }
    
    .separator {
      display: none;
    }
  }
`;

export default function Nav() {
  const { theme, toggleTheme } = useContext(ThemeContext);
  return (
    <NavStyles>
      <div className="top-left">
        <Link className="about" to="/about">
        {theme === 'dark' ? '> about' : 'About'}
        </Link>
      </div>
      <Logo />
      <div className="top-right">
        <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
            {theme === 'dark' ? '[ ☀ ]' : <MoonIcon className="outline"/>}
        </button>
      </div>
      <ul className="nav">
        <li>
          <Link to="/fabrication">
            {theme === 'dark' ? '[ FABRICATION ]' : 'FABRICATION'}
          </Link>
        </li>
        {theme === 'light' && <li className="separator">✦</li>}
        <li>
          <Link to="/digital">
            {theme === 'dark' ? '[ DIGITAL ]' : 'DIGITAL'}
          </Link>
        </li>
        {theme === 'light' && <li className="separator">✦</li>}
        <li>
          <Link to="/workshop">
            {theme === 'dark' ? '[ WORKSHOP ]' : 'WORKSHOP'}
          </Link>
        </li>
      </ul>
    </NavStyles>
  );
}
